<template>
  <validation-observer ref="containerForm" v-slot="{ invalid }">
    <h5>Supplier: <br>{{ supplier.booking_name }}</h5>

    <hr />

    <h5>Product: <br>{{ product.name }}</h5>

    <form @submit.prevent="handleSubmit">
      <template v-if="product">
        <hr />

        <validation-provider name="Supplier" v-slot="context" rules="required">
          <multi-select
            v-model="selectedVariations"
            placeholder="Pick a variation"
            track-by="id"
            label="variation_place_name"
            :options="allVariations"
            :multiple="true"
            :preserve-search="true"
            :close-on-select="false"
            :clear-on-select="false"
          />

          <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
            {{ context.errors[0] }}
          </small>
        </validation-provider>
        <hr />

        <b-form-group label="Container status">
          <b-badge v-if="isDeleted" variant="secondary">Deleted</b-badge>
          <b-form-checkbox v-else v-model="status" switch>
            Active
          </b-form-checkbox>
          <small class="text-muted">
            <template v-if="isDeleted">Deleted containers cannot be activated.</template>
            <template v-else>Inactive containers do not issue new codes. Previously issued codes remain available.</template>
          </small>
        </b-form-group>

        <hr />

        <validation-provider name="Valid For" v-slot="context" rules="required">
          <b-form-group label="Valid For (Participants)">
            <b-form-input
              v-model="valid_for"
              placeholder="e.g 1"
              :state="getValidationState(context)"
              v-bind="context.ariaInput"
            />

            <b-form-invalid-feedback v-bind="context.ariaMsg">
              {{ context.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <validation-provider name="Templates" rules="required">
          <b-form-group label="Templates">
            <b-tabs>
              <b-tab title="Email">
                <wysiwyg-editor v-model="email_template" />
              </b-tab>
              <b-tab title="PDF" active>
                <wysiwyg-editor v-model="pdf_template" />
              </b-tab>
            </b-tabs>
          </b-form-group>
        </validation-provider>

        <b-row>
          <b-col>
            <validation-provider name="PDF Title" v-slot="context">
              <b-form-group label="PDF Title">
                <b-form-input
                  v-model="pdf_title"
                  placeholder="e.g Lorem ipsum"
                  :state="getValidationState(context)"
                  v-bind="context.ariaInput"
                />

                <b-form-invalid-feedback v-bind="context.ariaMsg">
                  {{ context.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col>
            <validation-provider name="PDF Link Text" v-slot="context">
              <b-form-group label="PDF Link Text">
                <b-form-input
                  v-model="pdf_link_text"
                  placeholder="e.g Dolor Sit Amet"
                  :state="getValidationState(context)"
                  v-bind="context.ariaInput"
                />

                <b-form-invalid-feedback v-bind="context.ariaMsg">
                  {{ context.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <div class="mb-2">Batches Settings</div>

        <template v-if="batches">
          <template v-for="(batch, index) in batches">
            <div class="my-3" :key="index">
              <b-row>
                <b-col>
                  <b-input-group class="d-flex">
                    <validation-provider name="Batch" v-slot="context" rules="required" class="flex-grow-1">
                      <multi-select
                        v-model="batches[index].batch"
                        placeholder="Pick a batch"
                        :options="titles"
                        track-by="id"
                        label="title"
                      />

                      <small v-if="context.errors.length" v-bind="context.ariaMsg" class="text-danger form-text">
                        {{ context.errors[0] }}
                      </small>
                    </validation-provider>

                    <template #append>
                      <b-button
                        variant="danger"
                        @click="() => removeBatch(index)"
                      >
                        <strong>&times;</strong>
                      </b-button>
                    </template>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col>
                  <b-form-checkbox v-model="batches[index].generate_barcodes" switch>
                    Generate barcodes
                  </b-form-checkbox>
                </b-col>

                <b-col>
                  <b-form-checkbox v-model="batches[index].add_prefix_or_suffix" switch>
                    Add prefix or suffix
                  </b-form-checkbox>
                </b-col>
              </b-row>

              <b-row v-if="batches[index].add_prefix_or_suffix" class="mt-1">
                <b-col>
                  <b-form-group label="Add prefix">
                    <b-form-input
                      placeholder="e.g -M"
                      v-model="batches[index].code_prefix"
                      style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    />
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group label="Add suffix">
                    <b-form-input
                      placeholder="e.g -M"
                      v-model="batches[index].code_suffix"
                      style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </template>
        </template>

        <b-button variant="light" size="sm" class="mt-2" @click="addEmptyBatch">
          Add Batch
        </b-button>

        <b-row align-h="end">
          <b-col cols="auto">
            <b-button type="submit" variant="primary" :disabled="$cannot('supplier.manage') || invalid || submitting">
              {{ submitting ? 'Updating...' : 'Update' }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </form>
  </validation-observer>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css'
import MultiSelect from 'vue-multiselect'
import {getValidationState} from '../page-helpers'
import {containers, products} from '@/api'
import WysiwygEditor from '@/components/WysiwygEditor';
import {
  activateWithConflicts,
  buildContainerPayload,
  conflictMessage,
  containerErrorMessage,
  findOverlappingActiveContainers,
  loadAllContainers,
} from './csv-container-status'

export default {
  name: 'CSVCodesBatchForm',
  props: {container: {type: Object}},
  inject: ['$cannot'],
  components: { WysiwygEditor, MultiSelect },

  beforeMount() {
    this.fetchBatchTitles()
  },

  data() {
    return {
      titles: [],
      selectedVariations: this.container.item.product_variations,
      batches: this.container.item.batches,
      product: this.container.item.product,
      allVariations: this.container.item.all_variations,
      supplier: this.container.item.supplier,

      valid_for: this.container.item.valid_for,
      email_template: this.container.item.email_template,
      pdf_template: this.container.item.pdf_template,
      pdf_title: this.container.item.pdf_title,
      pdf_link_text: this.container.item.pdf_link_text,
      status: this.container.item.status,
      originalStatus: this.container.item.status,
      submitting: false,
    }
  },

  computed: {
    isDeleted() {
      return this.container.item.deleted_at !== null && this.container.item.deleted_at !== undefined
    },
  },

  methods: {
    getValidationState,

    async fetchBatchTitles() {
      await products.variations.batches
        .titles()
        .then((res) => {
          this.titles = res.data
          this.$forceUpdate();
        })
    },

    addEmptyBatch() {
      this.batches.push({
        text: '',
        generate_barcodes: false,
        add_prefix_or_suffix: false,
        code_prefix: '',
        code_suffix: '',
      })
    },

    removeBatch(index) {
      if (this.batches.length === 1) {
        this.batches = []
        return
      }

      this.batches.splice(index, 1)
    },

    containerPayload(status = this.status) {
      return buildContainerPayload({
        status,
        product_id: this.product.id,
        product_variations: this.selectedVariations,
        valid_for: this.valid_for,
        email_template: this.email_template,
        pdf_template: this.pdf_template,
        pdf_title: this.pdf_title,
        pdf_link_text: this.pdf_link_text,
        batches: this.batches,
      }, status)
    },

    async confirmReplacement(conflicts) {
      if (!conflicts.length) {
        return true
      }

      return this.$bvModal.msgBoxConfirm(conflictMessage(conflicts, this.selectedVariations), {
        title: 'Replace active containers?',
        okTitle: 'Deactivate and update',
        okVariant: 'warning',
        cancelTitle: 'Cancel',
      })
    },

    async handleSubmit() {
      this.submitting = true

      try {
        await this.$store.dispatch('auth/refreshToken')
        const payload = this.containerPayload()

        if (this.status) {
          const allContainers = await loadAllContainers(containers)
          const conflicts = findOverlappingActiveContainers(
            allContainers,
            this.selectedVariations,
            this.container.item.id,
          )

          if (!await this.confirmReplacement(conflicts)) {
            return
          }

          await activateWithConflicts(
            containers,
            { ...this.container.item, product_variations: this.selectedVariations },
            conflicts,
            payload,
          )
        } else {
          await containers.update(this.container.item.id, payload)
        }

        this.originalStatus = this.status
        this.$bvToast.toast(`CSV codes container updated.`, {
          title: 'CSV codes container updated',
          variant: 'primary',
        })

        this.$emit('close')
      } catch (error) {
        this.status = this.originalStatus

        if (error.errors && this.$refs.containerForm) {
          this.$refs.containerForm.setErrors(error.errors)
        }

        const progress = error.containerStatusProgress
        const deactivatedIds = progress?.deactivatedIds ?? []
        const partial = progress
          ? `${deactivatedIds.length ? ` Containers ${deactivatedIds.map((id) => `#${id}`).join(', ')} were deactivated.` : ''} Container #${this.container.item.id} was not activated with the new settings.`
          : ''

        this.$bvToast.toast(`${containerErrorMessage(error)}${partial}`, {
          title: progress ? 'Container replacement incomplete' : 'Container update failed',
          variant: 'danger',
        })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
